// The inspector (the Mac's InspectorViews): for the scene the caret is in (or the page's item) — title, synopsis,
// notes, Include in compile, and every field of the project with an editor by its type (FieldEditor) — then its open
// comments. A panel on the right; on a narrow screen a sheet over the page. Text saves on leaving a field or a second
// after typing stops; menus and switches at once. Each save is one edit of the fragment's document.
import { useEffect, useRef, useState } from 'react';
import {
  isNumberedChapter, journalPostsInTree, parseTags, POST_ONLY_FIELDS, postContaining, TAGS_FIELD, type DocumentEditor, type Fragment, type MetadataValue, type Project,
} from '@writing-suite/core';
import { planEdit } from '../store/plan.ts';
import { locksOf } from '../store/planModel.ts';
import type { LiveProject } from '../store/project.ts';
import { CommitInput, CommitTextArea, FieldEditor, VIRTUAL_TAGS_FIELD } from './FieldEditor.tsx';
import { FieldsDialog } from './FieldsDialog.tsx';
import { InspectorStory } from './story/InspectorStory.tsx';
import { InspectorAlternates } from './plan/Alternates.tsx';
import { AttachmentsSection } from './Attachments.tsx';
import { LookUpPanel } from './LookUp.tsx';
import { setInspectorTab, useLookUp } from '../store/lookUp.ts';

const DATE = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

export function Inspector({
  project,
  live,
  fragmentId,
  canWrite,
  mode,
  onClose,
}: {
  project: Project;
  live: LiveProject | null;
  fragmentId: string | null;
  canWrite: boolean;
  mode: 'panel' | 'sheet';
  onClose: () => void;
}) {
  const fragment = fragmentId ? project.fragment(fragmentId) : null;
  const { tab } = useLookUp();
  const details = fragment ? <InspectorBody key={fragment.id} project={project} live={live} fragment={fragment} canWrite={canWrite} /> : <p className="empty-note">Choose a scene to see its details.</p>;
  const body = tab === 'lookUp' ? <LookUpPanel project={project} live={live} canWrite={canWrite} /> : details;
  const heading = (
    <header className="inspector-head">
      <h2 id="inspector-title" className="eyebrow">
        Inspector
      </h2>
      <div className="inspector-tabs" role="tablist" aria-label="Inspector pages">
        <button type="button" role="tab" aria-selected={tab === 'details'} className={tab === 'details' ? 'current' : ''} onClick={() => setInspectorTab('details')}>Details</button>
        <button type="button" role="tab" aria-selected={tab === 'lookUp'} className={tab === 'lookUp' ? 'current' : ''} onClick={() => setInspectorTab('lookUp')} data-inspector-tab="lookUp">Dossier</button>
      </div>
      <button type="button" className="quiet-button" onClick={onClose}>
        {mode === 'sheet' ? 'Done' : 'Hide'}
      </button>
    </header>
  );
  if (mode === 'sheet') return <Sheet onClose={onClose}>{heading}{body}</Sheet>;
  return (
    <aside className="inspector" id="inspector" aria-labelledby="inspector-title">
      {heading}
      {body}
    </aside>
  );
}

function Sheet({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    return () => d?.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className="inspector sheet"
      id="inspector"
      aria-labelledby="inspector-title"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="sheet-body">{children}</div>
    </dialog>
  );
}

function InspectorBody({ project, live, fragment, canWrite }: { project: Project; live: LiveProject | null; fragment: Fragment; canWrite: boolean }) {
  const entities = project.entities();
  const write = canWrite && !!live;
  const [editingFields, setEditingFields] = useState(false);
  const id = fragment.id;
  const locks = locksOf(project);
  // A project from before tags (or made on the web) shows Tags too; the first tag added makes the field.
  const own = project.fields();
  // A journal post's section has no Status, number or publish date of its own.
  const manuscriptId = project.type === 'journal' ? project.manuscriptId() : null;
  const section = !!manuscriptId && (() => {
    const post = postContaining(id, journalPostsInTree(project.tree(manuscriptId)));
    return !!post && post.fragmentId !== id;
  })();
  const shown = section ? own.filter((f) => !POST_ONLY_FIELDS.has(f.id)) : own;
  const fields = own.some((f) => f.type === 'tags') || !write ? shown : [...shown, VIRTUAL_TAGS_FIELD];
  const setField = (fieldId: string, value: MetadataValue | null) => {
    if (fieldId === TAGS_FIELD && !own.some((f) => f.type === 'tags')) {
      planEdit(live!, (p) => { p.ensureTagsField(); p.setTags(id, value?.type === 'text' ? parseTags(value.value) : []); });
    } else live?.edit((e) => e.setMetadata(id, fieldId, value));
  };
  const comments = fragment.comments.filter((c) => c.status === 'open' && c.parentId === null);
  return (
    <div className="inspector-body" data-fragment-id={id}>
      <div className="field-row stacked">
        <label htmlFor="inspector-title-field" className="eyebrow">
          Title
        </label>
        {write ? (
          <CommitInput
            id="inspector-title-field"
            className="inspector-title"
            value={fragment.title}
            onCommit={(t) => {
              const trimmed = t.trim();
              if (trimmed) live!.edit((e) => e.setTitle(id, trimmed));
            }}
          />
        ) : (
          <p id="inspector-title-field" className="inspector-title">{fragment.title || 'Untitled'}</p>
        )}
      </div>
      <div className="field-row stacked">
        <label htmlFor="inspector-synopsis" className="eyebrow">
          Synopsis
        </label>
        <CommitTextArea id="inspector-synopsis" className="synopsis-field" value={fragment.synopsis} readOnly={!write} rows={4} onCommit={(t) => live?.edit((e) => e.setSynopsis(id, t))} />
      </div>
      <div className="field-row stacked">
        <label htmlFor="inspector-notes" className="eyebrow">
          Notes
        </label>
        <CommitTextArea id="inspector-notes" className="notes-field" value={fragment.notes} readOnly={!write} rows={5} onCommit={(t) => live?.edit((e) => e.setNotes(id, t))} />
      </div>
      <div className="field-row">
        <label htmlFor="inspector-locked">Locked</label>
        <input
          id="inspector-locked"
          type="checkbox"
          className="field-switch"
          checked={fragment.locked || locks.has(id)}
          disabled={!write || (locks.has(id) && !fragment.locked)}
          title={locks.has(id) && !fragment.locked ? 'Locked by a folder above it; unlock the folder' : 'A locked document isn’t changed, moved or trashed until it’s unlocked'}
          onChange={(e) => live?.edit((p) => (p as unknown as DocumentEditor).setLocked(id, e.target.checked))}
        />
      </div>
      {project.openers().has(id) ? (
        // A chapter or part without a number (a prologue): compile and the contents count past it.
        <div className="field-row">
          <label htmlFor="inspector-numbered">Numbered</label>
          <input
            id="inspector-numbered"
            type="checkbox"
            className="field-switch"
            checked={isNumberedChapter(fragment.metadata)}
            disabled={!write}
            title="Off for a prologue or interlude: it opens with its title alone, and the chapters after it are numbered as if it weren’t there"
            onChange={(e) => live?.edit((p) => (p as unknown as DocumentEditor).setNumbered(id, e.target.checked))}
          />
        </div>
      ) : null}
      <div className="field-row">
        <label htmlFor="inspector-include">Include in compile</label>
        <input
          id="inspector-include"
          type="checkbox"
          className="field-switch"
          checked={fragment.includeInCompile}
          disabled={!write}
          onChange={(e) => live?.edit((p) => p.setIncludeInCompile(id, e.target.checked))}
        />
      </div>
      {fields.length ? (
        <section className="inspector-fields" aria-labelledby="inspector-fields-title">
          <h3 id="inspector-fields-title" className="eyebrow">
            Fields
          </h3>
          {fields.map((field) => {
            const fieldId = `inspector-field-${field.id}`;
            const menu = (field.type === 'enum' && field.options.length > 0) || field.type === 'entity-ref';
            return (
              <div className="field-row" key={field.id} data-field-id={field.id}>
                {menu ? <span className="field-label">{field.name}</span> : <label htmlFor={fieldId} className="field-label">{field.name}</label>}
                <FieldEditor
                  id={fieldId}
                  field={field}
                  value={fragment.metadata[field.id]}
                  project={project}
                  entities={entities}
                  readOnly={!write}
                  onChange={(value) => setField(field.id, value)}
                />
              </div>
            );
          })}
          {live ? (
            <button type="button" className="link-button" onClick={() => setEditingFields(true)}>
              Edit Fields…
            </button>
          ) : null}
        </section>
      ) : null}
      {editingFields && live ? <FieldsDialog project={project} live={live} write={write} onClose={() => setEditingFields(false)} /> : null}
      <InspectorStory project={project} live={live} fragmentId={id} readOnly={!write} />
      <InspectorAlternates fragmentId={id} />
      <AttachmentsSection live={live} attachments={fragment.attachments} />
      {comments.length ? (
        <section className="inspector-comments" aria-labelledby="inspector-comments-title">
          <h3 id="inspector-comments-title" className="eyebrow">
            {comments.length === 1 ? '1 comment' : `${comments.length} comments`}
          </h3>
          <ul>
            {comments.map((c) => (
              <li key={c.id} className={`comment ${c.authorKind}`} data-comment-id={c.id}>
                <p className="comment-meta">
                  <span className="comment-author">{c.author}</span>
                  {Number.isFinite(c.createdAt?.getTime?.()) ? <span> · {DATE.format(c.createdAt)}</span> : null}
                </p>
                {c.quote ? <p className="comment-quote">“{c.quote}”</p> : null}
                <p className="comment-body">{c.body}</p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
